import { internalApiSlice, transformResponse } from "../../api/internalApiSlice";
import { Integration, Tripletex, TwentyFourSevenOffice, WooCommerce } from "@/types/general";

export const INVOICABLE_INTEGRATIONS = [
    "poweroffice",
    "tripletex",
    "twenty_four_seven_office",
] as const satisfies readonly Integration.ActiveState.Root["slug"][];

export const integrationsApi = internalApiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getIntegrations: builder.query<Integration.Root, void>({
            query: () => ({
                url: `integrations`,
            }),
            providesTags: [{ type: "Integrations", id: "LIST" }],
            transformResponse,
        }),

        connectIntegration: builder.mutation<
            Integration.ActiveState.Root,
            Integration.DTO.Create & {
                slug: string;
            }
        >({
            query: ({ slug, ...body }) => ({
                url: `integrations/${slug}`,
                method: "POST",
                data: body,
            }),
            invalidatesTags: [{ type: "Integrations", id: "LIST" }],
            transformResponse,
        }),

        detachIntegration: builder.mutation<void, string>({
            query: (slug) => ({
                url: `integrations/${slug}`,
                method: "DELETE",
                data: slug,
            }),
            invalidatesTags: [{ type: "Integrations", id: "LIST" }],
        }),

        getTripletexProjects: builder.query<Tripletex.Projects.Root[], void>({
            query: () => ({
                url: `tripletex/projects`,
            }),
            transformResponse,
        }),

        importTripletexData: builder.mutation<void, Tripletex.DTO.Import>({
            query: (data) => ({
                url: `data-transfer/integrations/tripletex/import`,
                method: "POST",
                data: {
                    entities: data.entities,
                    default_product_location: data.default_product_location,
                },
            }),
        }),

        importTwentyFoundSevenOfficeData: builder.mutation<void, TwentyFourSevenOffice.DTO.Import>({
            query: (data) => ({
                url: `data-transfer/integrations/twenty_four_seven_office/import`,
                method: "POST",
                data: {
                    entities: data.entities,
                    default_product_location: data.default_product_location,
                },
            }),
        }),

        getTwentyFourSevenOfficeSalesAccounts: builder.query<TwentyFourSevenOffice.SalesAccounts.Root[], void>({
            query: () => ({
                url: `twenty_four_seven_office/sales-accounts`,
            }),
            transformResponse,
        }),

        getTwentyFourSevenOfficeProjects: builder.query<TwentyFourSevenOffice.Projects.Root[], void>({
            query: () => ({
                url: `twenty_four_seven_office/projects`,
            }),
            transformResponse,
        }),

        updateTripletexConfiguration: builder.mutation<
            Integration.ActiveState.Tripletex,
            Tripletex.DTO.UpdateConfiguration
        >({
            query: (data) => ({
                url: `integrations/configuration`,
                method: "PUT",
                data: {
                    integration: "tripletex",
                    configuration: data,
                },
            }),
            transformResponse,
        }),

        replaceTripletexToken: builder.mutation<Integration.ActiveState.Tripletex, Tripletex.DTO.ReplaceToken>({
            query: (data) => ({
                url: `integrations/tripletex/token`,
                method: "PUT",
                data,
            }),
            transformResponse,
        }),

        connectWooCommerce: builder.mutation<WooCommerce.Root, WooCommerce.DTO.Connect>({
            query: (data) => ({
                url: `integrations/woocommerce`,
                method: "POST",
                data,
            }),
            invalidatesTags: [
                { type: "Integrations", id: "LIST" },
                { type: "Integrations", id: "WooCommerce" },
            ],
            transformResponse,
        }),

        updateWooCommerceConfiguration: builder.mutation<WooCommerce.Root, WooCommerce.DTO.UpdateConfiguration>({
            query: (data) => ({
                url: `integrations/woocommerce`,
                method: "PUT",
                data,
            }),
            invalidatesTags: [{ type: "Integrations", id: "WooCommerce" }],
            transformResponse,
        }),

        getWooCommerceConfiguration: builder.query<WooCommerce.Root, void>({
            query: () => ({
                url: `integrations/woocommerce`,
                method: "GET",
            }),
            providesTags: [{ type: "Integrations", id: "WooCommerce" }],
            transformResponse,
        }),
    }),
});

export const {
    useConnectIntegrationMutation,
    useGetIntegrationsQuery,
    useDetachIntegrationMutation,
    useImportTripletexDataMutation,
    useUpdateTripletexConfigurationMutation,
    useReplaceTripletexTokenMutation,
    useGetTripletexProjectsQuery,
    useImportTwentyFoundSevenOfficeDataMutation,
    useGetTwentyFourSevenOfficeSalesAccountsQuery,
    useGetTwentyFourSevenOfficeProjectsQuery,
    useConnectWooCommerceMutation,
    useUpdateWooCommerceConfigurationMutation,
    useGetWooCommerceConfigurationQuery,
} = integrationsApi;
